import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Box, Chip, Divider,
    CircularProgress, Alert, IconButton, Tabs, Tab
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const LANG_LABELS = {
    fr: { label: 'Français', flag: '🇫🇷' },
    en: { label: 'English', flag: '🇬🇧' },
    ar: { label: 'العربية', flag: '🇸🇦', rtl: true },
};

/**
 * Modal de validation des traductions automatiques.
 *
 * Props:
 *   open           : bool
 *   onClose        : () => void  — annuler
 *   onValidate     : (translations) => void  — valider avec les traductions (modifiées ou non)
 *   salonName      : string  — nom original saisi
 *   salonDescription : string  — description originale saisie
 *   isLoading      : bool  — true pendant le polling
 *   translations   : { name: {fr,en,ar}, description: {fr,en,ar} } | null
 */
const TranslationValidationModal = ({
    open,
    onClose,
    onValidate,
    salonName = '',
    salonDescription = '',
    salonAddress = '',
    isLoading = false,
    translations = null,
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const langs = ['fr', 'en', 'ar'];

    const [edited, setEdited] = useState({
        name: { fr: '', en: '', ar: '' },
        description: { fr: '', en: '', ar: '' },
        address: { fr: '', en: '', ar: '' },
    });

    useEffect(() => {
        if (translations) {
            setEdited({
                name: {
                    fr: translations.name?.fr || salonName,
                    en: translations.name?.en || salonName,
                    ar: translations.name?.ar || salonName,
                },
                description: {
                    fr: translations.description?.fr || salonDescription,
                    en: translations.description?.en || salonDescription,
                    ar: translations.description?.ar || salonDescription,
                },
                address: {
                    fr: translations.address?.fr || salonAddress,
                    en: translations.address?.en || salonAddress,
                    ar: translations.address?.ar || salonAddress,
                },
            });
        }
    }, [translations, salonName, salonDescription, salonAddress]);

    const handleChange = (field, lang, value) => {
        setEdited(prev => ({
            ...prev,
            [field]: { ...prev[field], [lang]: value },
        }));
    };

    const handleValidate = () => {
        onValidate(edited);
    };

    const currentLang = langs[activeTab];
    const isRtl = LANG_LABELS[currentLang]?.rtl;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <AutoAwesomeIcon color="primary" />
                        <Typography variant="h6" fontWeight={700}>
                            {t('pro_salon.translations.modal_title', 'Traductions automatiques')}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('pro_salon.translations.modal_subtitle', 'Vérifiez et modifiez les traductions générées automatiquement avant de valider.')}
                </Typography>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {isLoading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {t('pro_salon.translations.generating', 'Génération des traductions en cours…')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Alert
                            severity="info"
                            icon={<TranslateIcon />}
                            sx={{ mb: 2, borderRadius: 2 }}
                        >
                            {t('pro_salon.translations.alert_info', 'Ces traductions ont été générées automatiquement. Vous pouvez les modifier librement.')}
                        </Alert>

                        {/* Original values */}
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('pro_salon.translations.original', 'Valeur originale saisie')}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}><strong>{salonName}</strong></Typography>
                            {salonDescription && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {salonDescription}
                                </Typography>
                            )}
                            {salonAddress && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {salonAddress}
                                </Typography>
                            )}
                        </Box>

                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => setActiveTab(v)}
                            variant="fullWidth"
                            sx={{ mb: 2 }}
                        >
                            {langs.map(lang => (
                                <Tab
                                    key={lang}
                                    label={
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <span>{LANG_LABELS[lang].flag}</span>
                                            <span>{LANG_LABELS[lang].label}</span>
                                        </Box>
                                    }
                                />
                            ))}
                        </Tabs>

                        <Box dir={isRtl ? 'rtl' : 'ltr'}>
                            <TextField
                                label={t('pro_salon.translations.name', 'Nom')}
                                value={edited.name[currentLang]}
                                onChange={e => handleChange('name', currentLang, e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                sx={{ mb: 2 }}
                                InputProps={{
                                    endAdornment: (
                                        <Chip
                                            label={LANG_LABELS[currentLang].label}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ),
                                    style: isRtl ? { textAlign: 'right' } : {},
                                }}
                            />
                            <TextField
                                label={t('pro_salon.translations.description', 'Description')}
                                value={edited.description[currentLang]}
                                onChange={e => handleChange('description', currentLang, e.target.value)}
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    style: isRtl ? { textAlign: 'right' } : {},
                                }}
                            />
                            <TextField
                                label={t('pro_salon.translations.address', 'Adresse')}
                                value={edited.address[currentLang]}
                                onChange={e => handleChange('address', currentLang, e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                size="small"
                                sx={{ mt: 2 }}
                                InputProps={{
                                    style: isRtl ? { textAlign: 'right' } : {},
                                }}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isLoading}
                    color="inherit"
                >
                    {t('pro_salon.translations.skip', 'Ignorer')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleValidate}
                    disabled={isLoading}
                    startIcon={<CheckCircleIcon />}
                >
                    {t('pro_salon.translations.validate', 'Valider les traductions')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TranslationValidationModal;
